import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

const TemplateMinimal = ({ data }) => {
  if (!data) return null;
  const rawInvoice = data.invoice || data.Invoice;
  if (!rawInvoice) return null;

  // Normalize Invoice object to support both camelCase and PascalCase
  const invoice = {
    id: rawInvoice.id || rawInvoice.Id,
    clientId: rawInvoice.clientId || rawInvoice.ClientId,
    templateId: rawInvoice.templateId || rawInvoice.TemplateId,
    invoiceNo: rawInvoice.invoiceNo || rawInvoice.InvoiceNo,
    invoiceDate: rawInvoice.invoiceDate || rawInvoice.InvoiceDate,
    billerGSTIN: rawInvoice.billerGSTIN || rawInvoice.BillerGSTIN || '',
    billerAddress: rawInvoice.billerAddress || rawInvoice.BillerAddress || '',
    billerContact: rawInvoice.billerContact || rawInvoice.BillerContact || '',
    billToCompany: rawInvoice.billToCompany || rawInvoice.BillToCompany || '',
    billToName: rawInvoice.billToName || rawInvoice.BillToName || '',
    billToAddress: rawInvoice.billToAddress || rawInvoice.BillToAddress || '',
    billToGSTIN: rawInvoice.billToGSTIN || rawInvoice.BillToGSTIN || '',
    billToEmail: rawInvoice.billToEmail || rawInvoice.BillToEmail || '',
    subTotal: rawInvoice.subTotal || rawInvoice.SubTotal || 0,
    totalCGST: rawInvoice.totalCGST || rawInvoice.TotalCGST || rawInvoice.totalCgst || rawInvoice.TotalCgst || 0,
    totalSGST: rawInvoice.totalSGST || rawInvoice.TotalSGST || rawInvoice.totalSgst || rawInvoice.TotalSgst || 0,
    grandTotal: rawInvoice.grandTotal || rawInvoice.GrandTotal || 0,
    paymentStatus: rawInvoice.paymentStatus || rawInvoice.PaymentStatus || 'Unpaid',
    templateComponent: rawInvoice.templateComponent || rawInvoice.TemplateComponent || '',
    billerCompany: rawInvoice.billerCompany || rawInvoice.BillerCompany || 'Intechronix',
    billerLogoUrl: rawInvoice.billerLogoUrl || rawInvoice.BillerLogoUrl || ''
  };

  const rawItems = data.items || data.Items || [];
  const items = rawItems.map(item => ({
    description: item.description || item.Description || '',
    hsnSacCode: item.hsnSacCode || item.HsnSacCode || '',
    qty: item.qty || item.Qty || 0,
    netPrice: item.netPrice || item.NetPrice || 0,
    gstPercentage: item.gstPercentage || item.GstPercentage || 0,
    cgstAmount: item.cgstAmount || item.CgstAmount || 0,
    sgstAmount: item.sgstAmount || item.SgstAmount || 0,
    totalItemPrice: item.totalItemPrice || item.TotalItemPrice || 0
  }));

  const status = invoice.paymentStatus.toUpperCase();
  const statusColor = status === 'PAID' ? '#10B981' : '#EF4444';

  const seller = invoice.billerCompany;
  const invNo = invoice.invoiceNo;
  const billTo = invoice.billToCompany;
  const total = invoice.grandTotal.toFixed(2);
  const gst = invoice.billToGSTIN || 'N/A';
  const primaryColor = invoice.templateColor || '#0f172a';

  const qrData = `SELLER: ${seller}\nINV NO: ${invNo}\nBILL TO: ${billTo}\nGSTIN: ${gst}\nTOTAL: ₹${total}\nSTATUS: ${status}`;

  return (
    <div style={{ 
        padding: '12mm', 
        background: '#fff', 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', 
        color: '#334155', 
        position: 'relative', 
        overflow: 'hidden', 
        width: '210mm', 
        height: '297mm', 
        margin: '0 auto', 
        boxSizing: 'border-box',
        fontSize: '10.5pt',
        lineHeight: '1.4'
    }}>
      {/* Small Status Indicator */}
      <div style={{ 
        position: 'absolute', 
        top: '20mm', 
        right: '40mm', 
        padding: '4px 12px', 
        borderRadius: '20px', 
        fontSize: '0.8rem', 
        fontWeight: '700', 
        color: '#fff', 
        backgroundColor: statusColor,
        zIndex: 2,
        textTransform: 'uppercase'
      }}>
        {status}
      </div>

      <div style={{ marginBottom: '2rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '2.5rem', fontWeight: '200', color: primaryColor, letterSpacing: '-1px' }}>INVOICE</div>
          <div style={{ color: '#64748b' }}>#{invNo}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
           <QRCodeSVG value={qrData} size={70} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        <div>
          <div style={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.5px' }}>From</div>
          <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '1.1rem' }}>{seller}</div>
          <div style={{ whiteSpace: 'pre-line', color: '#475569' }}>{invoice.billerAddress}</div>
          <div style={{ marginTop: '4px', fontWeight: '600' }}>GSTIN: {invoice.billerGSTIN}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.5px' }}>To</div>
          <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '1.1rem' }}>{invoice.billToCompany}</div>
          {invoice.billToName && <div style={{ fontWeight: '500' }}>{invoice.billToName}</div>}
          <div style={{ whiteSpace: 'pre-line', color: '#475569' }}>{invoice.billToAddress}</div>
          {invoice.billToGSTIN && <div style={{ marginTop: '4px', fontWeight: '600' }}>GSTIN: {invoice.billToGSTIN}</div>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4rem', marginBottom: '2rem' }}>
        <div>
          <div style={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.5px' }}>Date</div>
          <div style={{ fontWeight: '600' }}>{new Date(invoice.invoiceDate).toLocaleDateString()}</div>
        </div>
      </div>

      <div style={{ borderTop: `2px solid ${primaryColor}`, borderBottom: '1px solid #e2e8f0', minHeight: '300px' }}>
        {items?.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: i === items.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#1e293b' }}>{item.description}</div>
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                {item.hsnSacCode && `HSN: ${item.hsnSacCode} | `}
                {item.qty} × ₹{item.netPrice.toFixed(2)} (+{item.gstPercentage}% GST)
              </div>
            </div>
            <div style={{ fontWeight: '700', color: '#0f172a', width: '120px', textAlign: 'right' }}>₹{item.totalItemPrice.toFixed(2)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <div style={{ width: '250px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#64748b' }}>
             <span>Sub Total</span><span>₹{invoice.subTotal?.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#64748b' }}>
             <span>Tax Amount</span><span>₹{(invoice.totalCGST + invoice.totalSGST)?.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontSize: '1.3rem', fontWeight: '800', borderTop: `2px solid ${primaryColor}`, marginTop: '8px', color: primaryColor }}>
             <span>Total</span><span>₹{invoice.grandTotal?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 'auto', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
        <div style={{ fontSize: '0.85rem' }}>
          <div style={{ fontWeight: '700', marginBottom: '4px' }}>Terms & Conditions:</div>
          <div style={{ color: '#64748b' }}>
            Payment due within agreed time. GST as applicable. Subject to Gorakhpur jurisdiction.
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#94a3b8', letterSpacing: '0.5px' }}>
          THIS IS A COMPUTER GENERATED DOCUMENT AND REQUIRES NO SIGNATURE.
        </div>
      </div>
    </div>
  );
};
export default TemplateMinimal;
